import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { openSearchScope, openMixpediaLabel } from '../../redux/thunk/appNavThunk';
import {
    setActiveSearch,
    setSearchAllScope,
    setArtistQuery,
    setTrackQuery,
    setMixQuery,
    setMixFilterContext,
    setTrackListQuery,
} from '../../redux/slices/djpalSlice';
import {
    searchArtist,
    searchTrack,
    searchMixes,
    searchTracksList,
    ensureToken,
} from '../../redux/thunk/djpalThunk';
import MultiArtistText, { dedupeCombinedArtists } from '../MultiArtistText';
import TileScroller from './TileScroller';
import DJPalAudioPreview from './DJPalAudioPreview';
import { useMixPlayback, canPlayMix } from './MixPlayer';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { entityId } from '../../utils/entityId';
import {
    eventKindLabel, eventMixesHeading, eventSectionHeader, normalizeEventKind,
    foldText, buildOwnSeriesChecker, bestOwningArtistScore, scoreArtist, scoreEvent, scoreTrack, scoreMix,
    isRealArtist,
} from '../../utils/searchSections';
import { eventFilters, eventFilterContext } from '../../utils/eventNav';
import { track as trackEvent } from '../../utils/analytics';
import PlayAllButton from '../common/PlayAllButton';
import CoverArt from '../CoverArt';
import './SearchResultsPage.scss';
import { useDesignV2 } from '../../utils/designV2';
import { SectionHeader } from '../v2/PageTabs';
import { fmtNum, relativeDate } from '../v2/format';
import FollowArtistButton from '../FollowArtistButton';

const SECTION_LIMIT = 6;

// Scoring lives in utils/searchSections (shared with the dropdown's section
// ordering, so the two surfaces never disagree about what ranks first).
// Design notes that shaped it:
// - Scale beats entity-ness: "tomorrowland" is 99% a festival query even
//   though a verified Apple artist row exists — an event with a real mix
//   corpus (+20 at ≥5 mixes) outranks a mix-less artist on equal text match.
// - Events are scored on their VENUE BASE too ("Space, Ibiza" must win the
//   query "space" over the parser-artifact bare entity "Space").
// - An event whose name IS (or starts with) a mix-owning artist's name is
//   that artist's own series ("Keinemusik Radio Show") — no scale bonus, so
//   the artist wins. Tomorrowland is unaffected (its artist row owns no
//   mixes, so it gets no artist bonus either).
const pickTopResult = (groups, q) => {
    const candidates = [];
    const isOwnSeries = buildOwnSeriesChecker(groups.artists || []);
    const artistBest = bestOwningArtistScore(groups.artists || [], q);
    (groups.events || []).slice(0, 4).forEach(ev => {
        candidates.push({ type: 'event', item: ev, score: scoreEvent(ev, q, isOwnSeries) });
    });
    (groups.artists || []).slice(0, 3).forEach(a => {
        candidates.push({ type: 'artist', item: a, score: scoreArtist(a, q) });
    });
    // Artist-derived credit caps at artistBest - 20, so it can reorder the
    // SECTIONS but never flip the top card away from the artist itself.
    (groups.tracks || []).slice(0, 3).forEach(t => {
        candidates.push({ type: 'track', item: t, score: scoreTrack(t, q, artistBest) });
    });
    (groups.mixes || []).slice(0, 3).forEach(m => {
        candidates.push({ type: 'mix', item: m, score: scoreMix(m, q, artistBest, isOwnSeries) });
    });
    // Tie-break on corpus size: same text match, more mixes wins.
    candidates.sort((a, b) => (b.score - a.score) || ((b.item.mixCount || 0) - (a.item.mixCount || 0)));
    const top = candidates[0] || null;
    // An "artist" that owns no mixes but whose name matches mixes is an
    // EVENT/festival entity (Tomorrowland: verified Apple artist, 0 mixes by
    // it, 655 mixes titled with it). Its artist page would be empty — the
    // useful destination is the full title-matched mixes list, so surface an
    // event-style card that opens the Mixes scope. The artist stays
    // reachable from the Artists section below.
    if (top && top.type === 'event') {
        const ev = top.item;
        return {
            type: 'event',
            item: {
                name: ev.name,
                kind: ev.kind,
                mixCount: ev.mixCount,
                covers: (ev.covers || []).slice(0, 4),
            },
        };
    }
    // Fallback heuristic (no server event matched): an "artist" that owns no
    // mixes but whose name matches mixes is an event-like entity — its artist
    // page would be empty, the mixes list is the useful destination.
    if (top && top.type === 'artist' && !isRealArtist(top.item) && (groups.mixes || []).length > 0) {
        const covers = [...new Set(groups.mixes.map(m => m.artworkUrl).filter(Boolean))].slice(0, 4);
        return {
            type: 'event',
            item: { name: top.item.name, covers },
        };
    }
    return top;
};

const Artwork = ({ src, alt = '', shape = 'square', fallback, size = 56 }) => (
    <div className={`sr-art sr-art-${shape}`}>
        {src ? <CoverArt src={src} size={size} alt={alt} /> : <div className="sr-art-placeholder">{fallback}</div>}
    </div>
);

// Session-scoped result cache keyed by query. Persists across mounts so
// browser back/forward into /search?q=... skips the network round-trip.
const searchResultCache = new Map();

const SearchResultsPage = ({ query }) => {
    const dispatch = useDispatch();
    const store = useStore();
    const v2 = useDesignV2(); // DESIGN V2 — chips, row-card top result, row lists
    const cached = query ? searchResultCache.get(query) : null;
    const [loading, setLoading] = useState(!cached);
    const [retryTick, setRetryTick] = useState(0);
    const [groups, setGroups] = useState(cached?.groups || { artists: [], tracks: [], mixes: [] });
    const [mixesByArtistServer, setMixesByArtistServer] = useState(cached?.mixesByArtistServer || []);
    const [featuringMixes, setFeaturingMixes] = useState(cached?.featuringMixes || []);
    // Mixes that PLAYED the top-result track (server /mixes trackId filter) —
    // the track equivalent of "Featuring <artist>". Title-matched groups.mixes
    // can't answer "who dropped this track", which is the reason to search a
    // track name in the first place.
    const [trackMixes, setTrackMixes] = useState(cached?.trackMixes || []);
    // Mixes belonging to an event/venue top result. The unified autocomplete
    // caps its mix list at 5 title-prefix matches, so an event with 600 mixes
    // rendered a 5-tile row whose scroll arrow moved ~18px and stopped.
    const [eventMixes, setEventMixes] = useState(cached?.eventMixes || []);
    // Server-resolved alias canonical (e.g. "dj daddy trance" → "marlon
    // hoffstadt"). Must be state — the render-time top pick scores against it.
    const [resolvedQuery, setResolvedQuery] = useState(cached?.resolvedQuery || null);
    // Scope pill filter: 'all' | 'artists' | 'tracks' | 'mixes' (Spotify's
    // results-page chips). A non-'all' scope isn't just a filter — it
    // upgrades the category to the FULL paginated search (the autocomplete
    // groups only carry 5-10 items, which made the pills feel valueless).
    // Lives in the djpal slice so pill clicks join Back/Forward history.
    const scope = useSelector((state) => state.djpal.searchAllScope || 'all');
    const [scopeFull, setScopeFull] = useState({ tracks: null, mixes: null });
    const scopeFetchSeqRef = useRef(0);
    const scopeSentinelRef = useRef(null);
    const [error, setError] = useState(null);
    const fetchSeqRef = useRef(0);
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    // Mix playback — same pattern as MixpediaHome's hover-play cards.
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    const isPlayerPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    const pausePlayback = (e) => {
        e.stopPropagation();
        const a = document.querySelector('audio');
        if (a && !a.paused) a.pause();
    };
    // Clicking play on the loaded-but-paused mix resumes in place instead of
    // re-extracting and restarting from 0:00.
    const resumeIfCurrent = (matches) => {
        if (!matches) return false;
        const a = document.querySelector('audio');
        if (a && a.paused) { a.play().catch(() => {}); return true; }
        return false;
    };

    // No page header — the navbar home button / search-clear × replaced the
    // per-page close, and the query is already visible in the navbar box.

    useEffect(() => {
        if (!query || query.length < 2) {
            setGroups({ artists: [], tracks: [], mixes: [] });
            setFeaturingMixes([]);
            setMixesByArtistServer([]);
            setTrackMixes([]);
            setEventMixes([]);
            setResolvedQuery(null);
            setLoading(false);
            return;
        }
        const hit = searchResultCache.get(query);
        if (hit) {
            setGroups(hit.groups);
            setMixesByArtistServer(hit.mixesByArtistServer);
            setFeaturingMixes(hit.featuringMixes);
            setTrackMixes(hit.trackMixes || []);
            setEventMixes(hit.eventMixes || []);
            setResolvedQuery(hit.resolvedQuery || null);
            setLoading(false);
            setError(null);
            return;
        }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        setError(null);
        setFeaturingMixes([]);
        setMixesByArtistServer([]);
        setTrackMixes([]);
        setEventMixes([]);
        setResolvedQuery(null);
        (async () => {
            try {
                const token = await ensureToken(dispatch, store.getState);
                // eventLimit: this page has room for the whole Events & venues
                // shelf, unlike the navbar dropdown.
                const result = await window.electron.djpal.autocompleteUnified(token, query, { eventLimit: 12 });
                if (seq !== fetchSeqRef.current) return;
                if (result.error) {
                    trackEvent('search_failed', { query, reason: String(result.error).slice(0, 80) });
                    setError(result.error);
                    setGroups({ artists: [], tracks: [], mixes: [] });
                    return;
                }

                const base = {
                    artists: result.artists || [],
                    tracks: result.tracks || [],
                    mixes: result.mixes || [],
                    events: result.events || [],
                    // Servers that predate label search send no `labels`.
                    labels: result.labels || [],
                };

                // When the server resolved an artist alias (e.g. "dj daddy
                // trance" → "marlon hoffstadt"), score against the canonical
                // so the aliased artist still wins the top pick.
                const scoringQuery = result.resolvedQuery || query;
                setResolvedQuery(result.resolvedQuery || null);

                // If the top artist has a different canonical name than the
                // query (e.g. "Tiësto" vs typed "tiesto"), the tracks/mixes
                // endpoints won't diacritic-fold on the server side — so
                // re-fetch with the canonical name and merge.
                const topPick = pickTopResult(base, scoringQuery);
                const canonical = topPick?.type === 'artist' ? topPick.item.name : null;
                if (canonical && foldText(canonical) === foldText(scoringQuery) && canonical !== scoringQuery) {
                    const extra = await window.electron.djpal.autocompleteUnified(token, canonical);
                    if (seq !== fetchSeqRef.current) return;
                    if (!extra.error) {
                        const mergeBy = (a, b, keyFn) => {
                            const seen = new Set(a.map(keyFn));
                            return [...a, ...b.filter(x => !seen.has(keyFn(x)))];
                        };
                        base.tracks = mergeBy(base.tracks, extra.tracks || [],
                            t => `${entityId(t) || t.title}|${t.artist || ''}`);
                        base.mixes = mergeBy(base.mixes, extra.mixes || [],
                            m => `${m.djName || ''}|${m.title}|${m.date || ''}`);
                        base.artists = mergeBy(base.artists, extra.artists || [],
                            a => entityId(a) || a.name);
                    }
                }

                setGroups(base);

                // High-confidence artist match: top pick is an artist AND its
                // canonical name matches the query (ignoring case/diacritics).
                // Fetch mixes that include tracks BY this artist — same API as
                // the artist page's "Featuring X" section.
                const finalTop = pickTopResult(base, scoringQuery);
                const isConfidentArtist =
                    finalTop?.type === 'artist' &&
                    foldText(finalTop.item.name) === foldText(scoringQuery);
                if (isConfidentArtist) {
                    const topArtistPid = entityId(finalTop.item);
                    const byFilters = topArtistPid
                        ? { artist: finalTop.item.name, artist_id: topArtistPid }
                        : { artist: finalTop.item.name };
                    const incFilters = topArtistPid
                        ? { artistIncluded: finalTop.item.name, artist_id: topArtistPid }
                        : { artistIncluded: finalTop.item.name };
                    const [byResp, incResp] = await Promise.all([
                        window.electron.djpal.searchMixes(token, byFilters, undefined, { page: 1, limit: 40 }),
                        window.electron.djpal.searchMixes(token, incFilters, undefined, { page: 1, limit: 40 }),
                    ]);
                    if (seq !== fetchSeqRef.current) return;
                    const extractMixes = (r) => {
                        if (!r || r.error) return [];
                        const d = r.data || r;
                        const arr = d.results || d.result;
                        return Array.isArray(arr) ? arr : (Array.isArray(d) ? d : []);
                    };
                    const byMixes = extractMixes(byResp);
                    const incMixes = extractMixes(incResp);
                    setMixesByArtistServer(byMixes);
                    setFeaturingMixes(incMixes);
                    searchResultCache.set(query, { groups: base, mixesByArtistServer: byMixes, featuringMixes: incMixes, trackMixes: [], eventMixes: [], resolvedQuery: result.resolvedQuery || null });
                } else if (finalTop?.type === 'track') {
                    // Track top result → the mixes that PLAYED it, by id
                    // (never text: a title alone collides across remixes).
                    // `_trackName` is the server's own name fallback when the
                    // id resolves to nothing.
                    const t = finalTop.item;
                    const tid = entityId(t);
                    const tName = t.artist ? `${t.artist} - ${t.title}` : t.title;
                    const filters = tid ? { trackId: tid, _trackName: tName } : { trackIncluded: tName };
                    const resp = await window.electron.djpal.searchMixes(token, filters, undefined, { page: 1, limit: 24 });
                    if (seq !== fetchSeqRef.current) return;
                    const d = (!resp || resp.error) ? {} : (resp.data || resp);
                    const arr = d.results || d.result;
                    const played = Array.isArray(arr) ? arr : [];
                    setTrackMixes(played);
                    searchResultCache.set(query, { groups: base, mixesByArtistServer: [], featuringMixes: [], trackMixes: played, eventMixes: [], resolvedQuery: result.resolvedQuery || null });
                } else if (finalTop?.type === 'event') {
                    // Event/venue top result → the event's own mixes (same
                    // title filter its card opens), not the 5 title-prefix
                    // rows the autocomplete happened to return.
                    const resp = await window.electron.djpal.searchMixesLite(
                        token, eventFilters(finalTop.item), undefined, { page: 1, limit: 24 });
                    if (seq !== fetchSeqRef.current) return;
                    const d = (!resp || resp.error) ? {} : (resp.data || resp);
                    const arr = d.results || d.result;
                    const evMixes = Array.isArray(arr) ? arr : [];
                    setEventMixes(evMixes);
                    searchResultCache.set(query, { groups: base, mixesByArtistServer: [], featuringMixes: [], trackMixes: [], eventMixes: evMixes, resolvedQuery: result.resolvedQuery || null });
                } else {
                    searchResultCache.set(query, { groups: base, mixesByArtistServer: [], featuringMixes: [], trackMixes: [], eventMixes: [], resolvedQuery: result.resolvedQuery || null });
                }
            } catch (e) {
                if (seq === fetchSeqRef.current) { trackEvent('search_failed', { query, reason: String(e.message || 'unknown').slice(0, 80) }); setError(e.message || 'Search failed'); }
            } finally {
                if (seq === fetchSeqRef.current) setLoading(false);
            }
        })();
    }, [query, dispatch, store, retryTick]);

    // New query: back to 'all', drop any fetched full-scope data. Skip the
    // mount run — a Back-restored snapshot arrives WITH its scope, and
    // resetting on mount would wipe it.
    const queryRef = useRef(query);
    useEffect(() => {
        if (queryRef.current === query) return;
        queryRef.current = query;
        dispatch(setSearchAllScope('all'));
        setScopeFull({ tracks: null, mixes: null });
        scopeFetchSeqRef.current++;
    }, [query, dispatch]);

    // The Mixes scope searches the RAW QUERY, deliberately. Scoping it to the
    // top entity instead ("space" → only "Space, Ibiza") loses the other half
    // of what the user asked for: "Space, Miami" is a different club, but
    // someone typing "space" wants both. The precise per-entity list stays one
    // click away — the top-result card, its labelled row, and the Events &
    // venues section all open a single entity.
    const mixScopeTitle = query;

    // Entering a Tracks/Mixes scope fetches the FULL paginated search for
    // that category (same endpoints the dedicated pages use).
    useEffect(() => {
        if (scope !== 'tracks' && scope !== 'mixes') return;
        if (scopeFull[scope]) return;
        const seq = ++scopeFetchSeqRef.current;
        (async () => {
            try {
                const token = await ensureToken(dispatch, store.getState);
                const r = scope === 'tracks'
                    ? await window.electron.djpal.searchTracks(token, query, 1, 30)
                    : await window.electron.djpal.searchMixes(token, { title: mixScopeTitle }, undefined, { page: 1, limit: 24 });
                if (seq !== scopeFetchSeqRef.current) return;
                const data = r?.data || r || {};
                const arr = data.results || data.result;
                const items = Array.isArray(arr) ? arr : [];
                const pg = data.pagination || {};
                setScopeFull(prev => ({ ...prev, [scope]: {
                    items,
                    page: pg.page || 1,
                    totalCount: pg.totalCount ?? items.length,
                    hasNext: (pg.page || 1) < (pg.totalPages || 1),
                } }));
            } catch { /* scope keeps the autocomplete fallback below */ }
        })();
    }, [scope, query, mixScopeTitle, scopeFull, dispatch, store]);

    const loadMoreScope = useCallback(async () => {
        const cur = scopeFull[scope];
        if (!cur || !cur.hasNext || cur.loadingMore) return;
        setScopeFull(prev => ({ ...prev, [scope]: { ...prev[scope], loadingMore: true } }));
        const seq = scopeFetchSeqRef.current;
        try {
            const token = await ensureToken(dispatch, store.getState);
            const nextPage = cur.page + 1;
            const r = scope === 'tracks'
                ? await window.electron.djpal.searchTracks(token, query, nextPage, 30)
                : await window.electron.djpal.searchMixes(token, { title: mixScopeTitle }, undefined, { page: nextPage, limit: 24 });
            if (seq !== scopeFetchSeqRef.current) return;
            const data = r?.data || r || {};
            const arr = data.results || data.result;
            const items = Array.isArray(arr) ? arr : [];
            const pg = data.pagination || {};
            setScopeFull(prev => ({ ...prev, [scope]: {
                items: [...(prev[scope]?.items || []), ...items],
                page: pg.page || nextPage,
                totalCount: pg.totalCount ?? prev[scope]?.totalCount,
                hasNext: (pg.page || nextPage) < (pg.totalPages || 1),
                loadingMore: false,
            } }));
        } catch {
            setScopeFull(prev => ({ ...prev, [scope]: { ...prev[scope], loadingMore: false } }));
        }
    }, [scope, scopeFull, query, mixScopeTitle, dispatch, store]);

    // Infinite scroll for the scoped full lists.
    useEffect(() => {
        if (scope !== 'tracks' && scope !== 'mixes') return undefined;
        const el = scopeSentinelRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMoreScope();
        }, { rootMargin: '400px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [scope, loadMoreScope]);

    const goArtist = useCallback((name, artistId = null, artworkUrl = null) => {
        if (!name) return;
        trackEvent('artist_opened', { artist_id: artistId || undefined, artist_name: name, from: 'search' });
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(name));
        dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: artworkUrl || null }));
    }, [dispatch]);

    const goTrack = useCallback((track) => {
        const q = track.artist ? `${track.artist} - ${track.title}` : track.title;
        trackEvent('track_opened', { track_id: entityId(track) || undefined, artist: track.artist, title: track.title, from: 'search' });
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(q));
        dispatch(searchTrack({ trackName: q, trackId: entityId(track), artworkUrl: track.artworkUrl || null }));
    }, [dispatch]);

    const goLabel = useCallback((l) => {
        const publicId = l?.publicId || l?.id;
        if (!publicId) return;
        trackEvent('search_result_clicked', { result_type: 'label', name: l.name });
        dispatch(openMixpediaLabel({ publicId, name: l.name, from: 'search' }));
    }, [dispatch]);

    const goTrackSearch = useCallback((q) => {
        dispatch(setActiveSearch('trackList'));
        dispatch(setTrackListQuery(q));
        dispatch(searchTracksList({ query: q }));
    }, [dispatch]);

    const goMix = useCallback((mix) => {
        const q = mix.djName ? `${mix.djName} - ${mix.title}` : mix.title;
        trackEvent('mix_opened', { mix_id: entityId(mix) || mix.id || undefined, artist: mix.djName, from: 'search' });
        // Id-only navigation (catalog rows always carry an id; link is the
        // exact-key fallback). Clicks never text-search.
        const mixId = entityId(mix);
        const filters = mixId ? { id: mixId }
            : mix.link ? { link: mix.link }
            : null;
        if (!filters) return;
        dispatch(setActiveSearch('mix'));
        dispatch(setMixFilterContext(null));
        dispatch(setMixQuery(q));
        dispatch(searchMixes({ filters }));
    }, [dispatch]);


    // "Show all Mixes by X" / "Show all Featuring X" → land on the Mixes
    // page with the appropriate server-side filter. Uses the same thunks
    // the artist page uses, so results match what users already know.
    const goArtistMixesBy = useCallback((name, publicId, artworkUrl = null) => {
        const filters = publicId ? { artist: name, artist_id: publicId } : { artist: name };
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setMixFilterContext({ kind: 'by', artistName: name, artistPublicId: publicId || null, artworkUrl: artworkUrl || null }));
        dispatch(searchMixes({ filters }));
    }, [dispatch]);

    // Event top-result click → title-filtered PAGINATED mixes list with the
    // event-name chip (matches the navbar's event-suggestion destination).
    const goEvent = useCallback((ev) => {
        const name = ev.name || '';
        if (!name) return;
        trackEvent('search_result_clicked', { result_type: 'event', name, kind: normalizeEventKind(ev.kind) });
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setMixFilterContext(eventFilterContext(ev)));
        dispatch(searchMixes({ filters: eventFilters(ev), paginate: true, lite: true }));
    }, [dispatch]);

    // "Show all" on the Played-in-mixes section → the paginated mixes list
    // filtered to the track. Carries trackId so infinite-scroll pages stay on
    // the same track instead of degrading to a name match.
    const goTrackMixes = useCallback((t) => {
        const name = t.artist ? `${t.artist} - ${t.title}` : t.title;
        const tid = entityId(t);
        trackEvent('search_result_clicked', { result_type: 'track_mixes', name });
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setMixFilterContext({ kind: 'trackIncluded', trackName: name, trackId: tid || null }));
        dispatch(searchMixes({ filters: tid ? { trackId: tid, _trackName: name } : { trackIncluded: name } }));
    }, [dispatch]);

    const goArtistFeaturing = useCallback((name, publicId, artworkUrl = null) => {
        const filters = publicId ? { artistIncluded: name, artist_id: publicId } : { artistIncluded: name };
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setMixFilterContext({ kind: 'featuring', artistName: name, artistPublicId: publicId || null, artworkUrl: artworkUrl || null }));
        dispatch(searchMixes({ filters }));
    }, [dispatch]);

    const firstChar = (s) => (s || '?').charAt(0).toUpperCase();

    if (loading) {
        // Skeleton mirroring the results layout (top-result card + track
        // rows + a tile row) — no "Searching…" copy that would then have to
        // flip to a no-results message.
        return (
            <div className="search-results-page">
                <div className="search-results-grid">
                    <div className="search-results-col">
                        <div className="sr-skel sr-skel-line sr-skel-heading" />
                        <div className="top-result-card sr-skeleton-card">
                            <div className="sr-skel sr-skel-art" />
                            <div className="sr-skel sr-skel-line sr-skel-line-lg" />
                            <div className="sr-skel sr-skel-line sr-skel-line-sm" />
                        </div>
                    </div>
                    <div className="search-results-col">
                        <div className="sr-skel sr-skel-line sr-skel-heading" />
                        <div className="search-results-track-list sr-skeleton-panel">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div className="sr-track-row sr-skeleton-row" key={i}>
                                    <div className="sr-skel sr-skel-tiny-art" />
                                    <div className="sr-track-info">
                                        <div className="sr-skel sr-skel-line sr-skel-line-md" />
                                        <div className="sr-skel sr-skel-line sr-skel-line-sm" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="search-results-section">
                    <div className="sr-skel sr-skel-line sr-skel-heading" />
                    <div className="sr-skeleton-tiles">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div className="sr-tile sr-skeleton-card" key={i}>
                                <div className="sr-skel sr-skel-tile-art" />
                                <div className="sr-skel sr-skel-line sr-skel-line-md" />
                                <div className="sr-skel sr-skel-line sr-skel-line-sm" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const hasAny = groups.artists.length || groups.tracks.length || groups.mixes.length || (groups.events || []).length || (groups.labels || []).length;

    if (!hasAny) {
        // A failed request is not "no results": say so and offer a retry.
        // (Groups are emptied on error, so without this branch a 401 or a
        // timeout read as a spelling problem.)
        return (
            <div className="search-results-page">
                <div className="search-results-empty">
                    <svg className="sr-empty-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
                        <line x1="16.2" y1="16.2" x2="21" y2="21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                    {error ? (
                        <>
                            <h2>Search didn't go through</h2>
                            <p>MixGoat couldn't reach the search server. Check your connection and try again.</p>
                            <button type="button" className="sr-retry-btn" onClick={() => { trackEvent('search_retry', { query }); setRetryTick((t) => t + 1); }}>Try again</button>
                        </>
                    ) : (
                        <>
                            <h2>No results for "{query}"</h2>
                            <p>Check the spelling, or try fewer or different keywords.</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Score against the alias-resolved canonical when the server gave one —
    // without it, "dj daddy trance" ranks a mix whose title merely CONTAINS
    // the alias above the artist Marlon Hoffstadt (the canonical the alias
    // maps to), because the raw query is no substring of the artist's name.
    const effectiveQuery = resolvedQuery || query;
    const top = pickTopResult(groups, effectiveQuery);
    // Stable slice so each track row's playContext.items refers to the exact
    // rendered list (index alignment matters for the queue). Scoped to
    // Tracks, the cap lifts — the pill IS the "give me more tracks" intent.
    const sectionTracks = groups.tracks.slice(0, SECTION_LIMIT);

    const renderTrackRow = (t, i, list) => (
        <button className="sr-track-row" key={entityId(t) || `t-${i}`} onClick={() => goTrack(t)}>
            <DJPalAudioPreview
                src={t.sampleUrl || t.previewUrl || t.preview_url || t.preview}
                youtubeId={t.youtubeId}
                soundcloudUrl={t.soundcloudUrl}
                trackId={entityId(t)}
                durationSec={t.durationSec ?? t.duration}
                pageUrl={t.pageUrl}
                previewId={`sr-track-${i}`}
                trackName={t.title}
                trackArtist={t.artist}
                trackCover={t.artworkUrl}
                playContext={{ items: list, index: i, type: 'tracks' }}
                playbackBlocked={t.playbackBlocked}
            >
                <Artwork src={t.artworkUrl} shape="tiny" fallback="♫" />
            </DJPalAudioPreview>
            <div className="sr-track-info">
                <span className="sr-track-title">{t.title}</span>
                {(t.artist || (t.artists && t.artists.length > 0)) && (
                    <span className="sr-track-artist">
                        <MultiArtistText
                            artists={t.artists}
                            fallbackText={t.artist}
                            segmentClassName="clickable-artist"
                            onSelect={(a) => goArtist(a.name, entityId(a), a.artworkUrl)}
                        />
                    </span>
                )}
            </div>
        </button>
    );

    const hasMixes = groups.mixes.length > 0 || mixesByArtistServer.length > 0 || featuringMixes.length > 0 || trackMixes.length > 0;
    // Venue-only / series-only result sets shouldn't be filed under "Events"
    // — the MV tags each entity, so the pill can say what it actually holds.
    const eventEntities = groups.events || [];
    const eventPillLabel = eventEntities.length && eventEntities.every(e => e.kind === 'venue')
        ? 'Venues'
        : eventEntities.length && eventEntities.every(e => e.kind === 'series')
            ? 'Series'
            : 'Events';
    // DESIGN V2 — counts on the chips (what each scope holds right now).
    // Only a number we know is a total: the autocomplete lists are capped
    // (5 tracks, 5 mixes, 12 events), so their lengths are not counts. A
    // scope that has been fetched carries the server's totalCount.
    const v2Counts = {
        artists: 0,
        tracks: scopeFull.tracks?.totalCount || 0,
        mixes: scopeFull.mixes?.totalCount || 0,
        events: 0,
        labels: 0,
    };
    const scopePills = (
        <div className={v2 ? 'v2-search-chips' : 'sr-scope-pills'}>
            {[['all', 'All', true],
              ['artists', 'Artists', groups.artists.length > 0],
              ['tracks', 'Tracks', groups.tracks.length > 0],
              ['mixes', 'Mixes', hasMixes],
              ['events', eventPillLabel, eventEntities.length > 0],
              ['labels', 'Labels', (groups.labels || []).length > 0]].map(([key, label, available]) => available && (
                <button
                    key={key}
                    type="button"
                    className={v2 ? `v2-chip${scope === key ? ' on' : ''}` : `sr-scope-pill ${scope === key ? 'active' : ''}`}
                    onClick={() => scope !== key && dispatch(openSearchScope(key))}
                >
                    {label}{v2 && key !== 'all' && v2Counts[key] > 0 && <small className="v2-num">{fmtNum(v2Counts[key])}</small>}
                </button>
            ))}
        </div>
    );

    const topOpen = () => {
        if (!top) return;
        if (top.type === 'event') goEvent(top.item);
        else if (top.type === 'artist') goArtist(top.item.name, entityId(top.item), top.item.artworkUrl);
        else if (top.type === 'track') goTrack(top.item);
        else goMix(top.item);
    };
    // DESIGN V2 — one facts line under the name: what it is, then what we
    // know about it ("Artist · Dance, Trance · 24 mixes"). Counts only when
    // the server said a total (the autocomplete lists are capped).
    const topMixTotal = top && top.type === 'artist' ? (top.item.mixCount || null) : null;
    const topFacts = top ? (
        top.type === 'event' ? [eventKindLabel(top.item.kind, top.item.type), top.item.mixCount ? `${fmtNum(top.item.mixCount)} mixes` : null]
        : top.type === 'artist' ? ['Artist', top.item.genres?.length ? top.item.genres.slice(0, 2).join(', ') : null, topMixTotal ? `${fmtNum(topMixTotal)} mixes` : null]
        : top.type === 'track' ? ['Track', top.item.artists?.length ? dedupeCombinedArtists(top.item.artists).map(a => a.name).join(', ') : (top.item.artist || null), trackMixes.length > 0 ? `played in ${fmtNum(trackMixes.length)} mixes` : null]
        : ['Mix', top.item.djName || null, top.item.date ? relativeDate(top.item.date) : null]
    ).filter(Boolean).join(' · ') : '';
    const topTrackIndex = top && top.type === 'track' ? sectionTracks.findIndex(t => t === top.item) : -1;
    const firstPlayableMix = (list) => list.find(m => canPlayMix(m) && m.link) || null;
    const topPlay = !top ? null
        : top.type === 'artist' ? (firstPlayableMix(mixesByArtistServer) ? () => { const m = firstPlayableMix(mixesByArtistServer); if (resumeIfCurrent(playingMixId === m.id)) return; playMix(m, { items: mixesByArtistServer, index: mixesByArtistServer.indexOf(m) }); }
            : sectionTracks.length > 0 ? () => window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: 'sr-track-0' })) : null)
        : top.type === 'track' ? (topTrackIndex >= 0 ? () => window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: `sr-track-${topTrackIndex}` })) : null)
        : top.type === 'mix' ? (canPlayMix(top.item) && top.item.link ? () => { if (resumeIfCurrent(playingMixId === top.item.id)) return; playMix(top.item, { items: [top.item], index: 0 }); } : null)
        : (firstPlayableMix(eventMixes) ? () => { const m = firstPlayableMix(eventMixes); if (resumeIfCurrent(playingMixId === m.id)) return; playMix(m, { items: eventMixes, index: eventMixes.indexOf(m) }); } : null);

    return (
        <div className="search-results-page">
            {error && <div className="search-results-error">{error}</div>}
            {scopePills}

            {scope === 'all' && (
            <div className="search-results-grid">
                {top && (
                    <div className="search-results-col">
                        {v2 ? <SectionHeader title="Top result" /> : <div className="search-results-section-title">Top result</div>}
                        <div
                            className="top-result-card"
                            role="button"
                            tabIndex={0}
                            onClick={topOpen}
                            onKeyDown={(e) => { if (e.key === 'Enter') topOpen(); }}
                        >
                            {top.type === 'event' ? (
                                // Collage at every count it can carry: 4 → 2x2,
                                // 3 → tall left + 2 stacked, 2 → halves, 1 →
                                // full-bleed, none → lettermark. Splitting only
                                // at 4 made a 2-mix venue look like it had one.
                                top.item.covers.length >= 2 ? (
                                    <div className={`sr-art sr-art-rounded sr-art-collage sr-art-collage-${Math.min(top.item.covers.length, 4)}`}>
                                        {top.item.covers.slice(0, 4).map((c, i) => <CoverArt src={c} size={80} eager key={i} />)}
                                    </div>
                                ) : (
                                    <Artwork src={top.item.covers[0] || null} shape="rounded" size={160} fallback={firstChar(top.item.name)} />
                                )
                            ) : (
                            <Artwork
                                src={top.item.artworkUrl}
                                shape={top.type === 'artist' ? 'circle' : 'rounded'}
                                fallback={top.type === 'artist' ? firstChar(top.item.name) : '♫'}
                            />
                            )}
                            <div className="top-result-name">
                                {top.type === 'artist' || top.type === 'event' ? top.item.name : top.item.title}
                            </div>
                            {v2 ? (
                                <div className="top-result-facts">{topFacts}</div>
                            ) : (
                            <div className="top-result-meta">
                                <span className="top-result-badge">
                                    {top.type === 'event' ? eventKindLabel(top.item.kind, top.item.type) : top.type === 'artist' ? 'Artist' : top.type === 'track' ? 'Track' : 'Mix'}
                                </span>
                                <span className="top-result-sub">
                                    {top.type === 'event'
                                        ? (top.item.mixCount ? `${top.item.mixCount} mixes` : 'All mixes')
                                        : top.type === 'artist'
                                        ? (top.item.genres?.length ? top.item.genres.join(', ') : '')
                                        : top.type === 'track'
                                            ? (top.item.artists?.length
                                                ? dedupeCombinedArtists(top.item.artists).map(a => a.name).join(', ')
                                                : (top.item.artist || ''))
                                            : (top.item.djName || '')}
                                </span>
                            </div>
                            )}
                            {v2 && (topPlay || top.type === 'artist') && (
                                <div className="top-result-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    {topPlay && (
                                        <button type="button" className="v2-btn primary" onClick={topPlay}>
                                            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="6 3 20 12 6 21 6 3"/></svg> Play
                                        </button>
                                    )}
                                    {top.type === 'artist' && (
                                        <FollowArtistButton prominent artist={{ name: top.item.name, artworkUrl: top.item.artworkUrl }} />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {groups.tracks.length > 0 && (
                    <div className="search-results-col">
                        {v2 ? (
                            <SectionHeader title="Tracks" count={scopeFull.tracks?.totalCount ? fmtNum(scopeFull.tracks.totalCount) : undefined} link="See all" onLink={() => goTrackSearch(query)} />
                        ) : (
                        <div className="sr-section-header sr-section-header-inline">
                            <div className="search-results-section-title">Tracks</div>
                            <button
                                type="button"
                                className="sr-show-all"
                                onClick={() => goTrackSearch(query)}
                            >
                                Show all
                            </button>
                        </div>
                        )}
                        <div className={`search-results-track-list${v2 ? ' v2-track-rows' : ''}`}>
                            {sectionTracks.map((t, i) => renderTrackRow(t, i, sectionTracks))}
                        </div>
                    </div>
                )}
            </div>
            )}

            {scope === 'tracks' && (() => {
                const full = scopeFull.tracks;
                // Skeletons while page 1 loads — rendering the 5 autocomplete
                // rows as a stopgap flashed "few then suddenly thirty".
                if (!full) {
                    return (
                        <div className="djpal-skel">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div className="djpal-skel-track-row" key={i}>
                                    <div className="djpal-skel-block skel-art" />
                                    <div className="skel-track-lines">
                                        <div className="djpal-skel-block skel-line" />
                                        <div className="djpal-skel-block skel-line short" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                }
                const list = full.items || [];
                if (!list.length) return null;
                // Show-all page idiom (flat rows, Play All + count) — no
                // "Tracks" heading, the active pill already says it.
                return (
                    <div className="search-results-col">
                        <div className="djpal-track-list-header">
                            <PlayAllButton tracks={list} from="search_results" />
                            {full.totalCount != null && (
                                <span className="djpal-track-list-count">
                                    {full.totalCount >= 500 ? '500+' : full.totalCount} {full.totalCount === 1 ? 'result' : 'results'}
                                </span>
                            )}
                        </div>
                        <div className="search-results-track-list sr-flat">
                            {list.map((t, i) => renderTrackRow(t, i, list))}
                        </div>
                        <div ref={scopeSentinelRef} />
                        {full.loadingMore && <div className="sr-scope-loading-more"><span className="mix-play-spinner" /></div>}
                    </div>
                );
            })()}

            {(() => {
                const topArtistName = top && top.type === 'artist' ? top.item.name : null;
                const topArtistId = top && top.type === 'artist' ? entityId(top.item) : null;
                const topArtistKey = topArtistName ? foldText(topArtistName) : null;
                // When the top result is a confident artist match (canonical
                // name equals the query ignoring case/diacritics), the
                // remaining artists are all longer prefix matches — noise.
                // Hide the section. Keep it otherwise so users can
                // disambiguate between similarly-named artists.
                const isConfidentArtist = topArtistName && topArtistKey === foldText(effectiveQuery);
                // Artists pill: show the FULL artist list (including the
                // confident top match — with the top-result card hidden it
                // must reappear here).
                const filteredArtists = scope === 'artists'
                    ? groups.artists
                    : isConfidentArtist
                        ? []
                        : (topArtistName
                            ? groups.artists.filter(a => foldText(a.name) !== topArtistKey)
                            : groups.artists);
                // With a confident artist match we have richer server-side
                // sets (same APIs the artist page uses). Fall back to the
                // autocomplete prefix matches otherwise.
                const mixesByArtist = topArtistName
                    ? (mixesByArtistServer.length > 0
                        ? mixesByArtistServer
                        : groups.mixes.filter(m => foldText(m.djName) === topArtistKey))
                    : [];
                // Event/venue and track top results get the same treatment as
                // an artist top result: a labelled mixes section with a
                // "Show all" into the matching paginated list.
                const topEvent = top && top.type === 'event' ? top.item : null;
                const topTrack = top && top.type === 'track' ? top.item : null;
                const keyOfMix = (m) => entityId(m) || m.id || `${m.djName}|${m.title}`;
                // Played-in-mixes already carries these; don't repeat them in
                // the generic title-match row below.
                const playedIds = new Set(trackMixes.map(keyOfMix));
                const mixesOther = topArtistName
                    ? (featuringMixes.length > 0
                        ? featuringMixes.filter(m => foldText(m.djName) !== topArtistKey)
                        : groups.mixes.filter(m => foldText(m.djName) !== topArtistKey))
                    : topEvent && eventMixes.length > 0
                        ? eventMixes
                        : topTrack
                            ? groups.mixes.filter(m => !playedIds.has(keyOfMix(m)))
                            : groups.mixes;
                const mixesSectionLabel = topArtistName
                    ? `Featuring ${topArtistName}`
                    : topEvent
                        ? eventMixesHeading(topEvent.kind, topEvent.name)
                        : 'Mixes';
                // Null → no button (a plain title match has no fuller list to
                // open beyond the Mixes pill).
                const mixesShowAll = topArtistName
                    ? () => goArtistFeaturing(topArtistName, topArtistId, top.item.artworkUrl)
                    : topEvent
                        ? () => goEvent(topEvent)
                        : null;

                // Sibling events/venues the top card isn't showing. These are
                // separate mix_events_search rows (it groups on the exact
                // event_name/location string), so "Club Vaag" and "Club Vaag,
                // Antwerpen" are two entities — dropping all but the top one
                // hid mixes that look like they belong to the venue.
                const topEventKey = topEvent ? foldText(topEvent.name) : null;
                const otherEvents = (groups.events || []).filter(e => foldText(e.name) !== topEventKey);

                const showMixes = scope === 'all';
                const showArtists = scope === 'all' || scope === 'artists';
                const showLabels = scope === 'all' || scope === 'labels';
                const labelEntities = groups.labels || [];
                // DESIGN V2 — never two half-width rows: each list is a full row of tiles.
                const showMixesSplit = showMixes && !v2 && mixesByArtist.length > 0 && mixesOther.length > 0;

                // div (not button) — the hover-play button nests inside, and
                // <button> inside <button> is invalid HTML.
                // v2 tile line: on "Mixes by X" the DJ is the heading, so the
                // line is where/when; elsewhere DJ · when. Dates read like the
                // rest of v2 ("Aug 16", "Nov 14, 2025"), not ISO.
                const tileSub = (m, keyPrefix) => {
                    if (!v2) return null;
                    const where = m.location && m.location !== m.title ? m.location : null;
                    const parts = keyPrefix === 'mb' || keyPrefix === 'smb'
                        ? [where, m.date ? relativeDate(m.date) : null]
                        : [m.djName, m.date ? relativeDate(m.date) : null];
                    const line = parts.filter(Boolean).join(' · ');
                    return line ? <div className="sr-tile-sub">{line}</div> : null;
                };
                // Preview rows lead with the mixes that have a picture; the
                // order inside the list is otherwise kept.
                const previewOrder = (list) => (v2 ? [...list].sort((a, b) => (resolveMixCover(b) ? 1 : 0) - (resolveMixCover(a) ? 1 : 0)) : list);
                const renderMixTile = (m, keyPrefix, i, list) => (
                    <div className="sr-tile" key={`${keyPrefix}-${i}`} role="button" tabIndex={0} onClick={() => goMix(m)}>
                        <div className="sr-tile-art-wrap">
                            <Artwork src={resolveMixCover(m)} shape="rounded" fallback="♫" />
                            {playingMixId === m.id && isPlayerPlaying ? (
                                <button className="sr-hover-play playing" title="Pause" onClick={pausePlayback}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                </button>
                            ) : (canPlayMix(m) && m.link && (
                                <button
                                    className="sr-hover-play"
                                    title="Play"
                                    onClick={(e) => { e.stopPropagation(); if (resumeIfCurrent(playingMixId === m.id)) return; playMix(m, { items: list, index: i }); }}
                                >
                                    {loadingMixId === m.id
                                        ? <span className="mix-play-spinner" />
                                        : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                </button>
                            ))}
                        </div>
                        <div className="sr-tile-title">{m.title}</div>
                        {v2 ? tileSub(m, keyPrefix) : (m.djName || m.date) && (
                            <div className="sr-tile-sub">
                                {m.djName && <span className="sr-tile-sub-name">{m.djName}</span>}
                                {m.date && <span className="sr-tile-sub-date">{m.djName ? '· ' : ''}{String(m.date).slice(0, 10)}</span>}
                            </div>
                        )}
                    </div>
                );

                return (
                    <>
                        {showMixesSplit && (
                            <div className="search-results-mixes-row">
                                <div className="search-results-section">
                                    {v2 ? <SectionHeader title={<>Mixes by {topArtistName}</>} link={(topArtistName) ? 'See all' : null} onLink={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)} /> : (
                                    <div className="sr-section-header">
                                        <h2 className="search-results-h2">Mixes by {topArtistName}</h2>
                                        {topArtistName && (
                                            <button type="button" className="sr-show-all" onClick={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)}>
                                                Show all
                                            </button>
                                        )}
                                    </div>
                                    )}
                                    <TileScroller>
                                        {mixesByArtist.map((m, i) => renderMixTile(m, 'mb', i, mixesByArtist))}
                                    </TileScroller>
                                </div>
                                <div className="search-results-section">
                                    {v2 ? <SectionHeader title={<>{mixesSectionLabel}</>} link={(topArtistName) ? 'See all' : null} onLink={() => goArtistFeaturing(topArtistName, topArtistId, top.item.artworkUrl)} /> : (
                                    <div className="sr-section-header">
                                        <h2 className="search-results-h2">{mixesSectionLabel}</h2>
                                        {topArtistName && (
                                            <button type="button" className="sr-show-all" onClick={() => goArtistFeaturing(topArtistName, topArtistId, top.item.artworkUrl)}>
                                                Show all
                                            </button>
                                        )}
                                    </div>
                                    )}
                                    <TileScroller>
                                        {mixesOther.map((m, i) => renderMixTile(m, 'mo', i, mixesOther))}
                                    </TileScroller>
                                </div>
                            </div>
                        )}

                        {showMixes && !showMixesSplit && mixesByArtist.length > 0 && (
                            <div className="search-results-section">
                                {v2 ? <SectionHeader title={<>Mixes by {topArtistName}</>} count={topMixTotal ? fmtNum(topMixTotal) : undefined} link={(topArtistName) ? 'See all' : null} onLink={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)} /> : (
                                <div className="sr-section-header">
                                    <h2 className="search-results-h2">Mixes by {topArtistName}</h2>
                                    {topArtistName && (
                                        <button type="button" className="sr-show-all" onClick={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)}>
                                            Show all
                                        </button>
                                    )}
                                </div>
                                )}
                                <TileScroller>
                                    {(() => { const l = previewOrder(mixesByArtist); return l.map((m, i) => renderMixTile(m, 'mb', i, l)); })()}
                                </TileScroller>
                            </div>
                        )}

                        {(showMixes || scope === 'mixes') && topTrack && trackMixes.length > 0 && (
                            <div className="search-results-section">
                                {v2 ? <SectionHeader title={<>Mixes that played &ldquo;{topTrack.title}&rdquo;</>} link={'See all'} onLink={() => goTrackMixes(topTrack)} /> : (
                                <div className="sr-section-header">
                                    <h2 className="search-results-h2">Mixes that played &ldquo;{topTrack.title}&rdquo;</h2>
                                    <button type="button" className="sr-show-all" onClick={() => goTrackMixes(topTrack)}>
                                        Show all
                                    </button>
                                </div>
                                )}
                                {scope === 'mixes'
                                    ? <div className="sr-browse-grid">{trackMixes.map((m, i) => renderMixTile(m, 'tm', i, trackMixes))}</div>
                                    : <TileScroller>{(() => { const l = previewOrder(trackMixes); return l.map((m, i) => renderMixTile(m, 'tm', i, l)); })()}</TileScroller>}
                            </div>
                        )}

                        {showMixes && !showMixesSplit && mixesOther.length > 0 && (
                            <div className="search-results-section">
                                {v2 ? <SectionHeader title={<>{mixesSectionLabel}</>} link={(mixesShowAll) ? 'See all' : null} onLink={mixesShowAll} /> : (
                                <div className="sr-section-header">
                                    <h2 className="search-results-h2">{mixesSectionLabel}</h2>
                                    {mixesShowAll && (
                                        <button type="button" className="sr-show-all" onClick={mixesShowAll}>
                                            Show all
                                        </button>
                                    )}
                                </div>
                                )}
                                <TileScroller>
                                    {(() => { const l = previewOrder(mixesOther); return l.map((m, i) => renderMixTile(m, 'mo', i, l)); })()}
                                </TileScroller>
                            </div>
                        )}

                        {scope === 'mixes' && (() => {
                            // Split kept (by-artist / featuring), but every
                            // section is the SAME idiom: wrapped grid. The
                            // deduped title matches follow as "More results".
                            const seenIds = new Set();
                            const keyOf = keyOfMix;
                            const mark = (arr) => { arr.forEach(m => seenIds.add(keyOf(m))); return arr; };
                            // ONLY lists that actually render may claim ids.
                            // Marking first and deciding to render later ate
                            // the whole page for an event top result: the
                            // split needs topArtistName, so nothing rendered,
                            // yet its 24 mixes were subtracted from `rest` —
                            // and `rest` is page 1 of the same title search.
                            const hasSplit = !!topArtistName && (mixesByArtist.length > 0 || mixesOther.length > 0);
                            // Rendered above by the played-in-mixes section.
                            if (topTrack) mark(trackMixes);
                            const byList = hasSplit ? mark(mixesByArtist) : [];
                            const otherList = hasSplit ? mark(mixesOther.filter(m => !seenIds.has(keyOf(m)))) : [];
                            const rest = (scopeFull.mixes?.items || []).filter(m => !seenIds.has(keyOf(m)));
                            // Page 1 in flight — skeletons, not a blank page
                            // (the Tracks scope already works this way).
                            if (!scopeFull.mixes && !hasSplit && !(topTrack && trackMixes.length > 0)) {
                                return (
                                    <div className="search-results-section">
                                        <div className="sr-skeleton-tiles">
                                            {Array.from({ length: 10 }).map((_, i) => (
                                                <div className="sr-tile sr-skeleton-card" key={i}>
                                                    <div className="sr-skel sr-skel-tile-art" />
                                                    <div className="sr-skel sr-skel-line sr-skel-line-md" />
                                                    <div className="sr-skel sr-skel-line sr-skel-line-sm" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <>
                                    {hasSplit && byList.length > 0 && (
                                        <div className="search-results-section">
                                            {v2 ? <SectionHeader title={<>Mixes by {topArtistName}</>} link={'See all'} onLink={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)} /> : (
                                            <div className="sr-section-header">
                                                <h2 className="search-results-h2">Mixes by {topArtistName}</h2>
                                                <button type="button" className="sr-show-all" onClick={() => goArtistMixesBy(topArtistName, topArtistId, top.item.artworkUrl)}>
                                                    Show all
                                                </button>
                                            </div>
                                            )}
                                            <div className="sr-browse-grid">
                                                {byList.map((m, i) => renderMixTile(m, 'smb', i, byList))}
                                            </div>
                                        </div>
                                    )}
                                    {hasSplit && otherList.length > 0 && (
                                        <div className="search-results-section">
                                            {v2 ? <SectionHeader title={<>Featuring {topArtistName}</>} link={'See all'} onLink={() => goArtistFeaturing(topArtistName, topArtistId, top.item.artworkUrl)} /> : (
                                            <div className="sr-section-header">
                                                <h2 className="search-results-h2">Featuring {topArtistName}</h2>
                                                <button type="button" className="sr-show-all" onClick={() => goArtistFeaturing(topArtistName, topArtistId, top.item.artworkUrl)}>
                                                    Show all
                                                </button>
                                            </div>
                                            )}
                                            <div className="sr-browse-grid">
                                                {otherList.map((m, i) => renderMixTile(m, 'smo', i, otherList))}
                                            </div>
                                        </div>
                                    )}
                                    {rest.length > 0 && (
                                        <div className="search-results-section">
                                            {/* With an event top result this list is WIDER than
                                                the venue row in All — it's every mix matching the
                                                text, across entities ("space" → Ibiza and Miami).
                                                Say so, or the two counts look like a bug. */}
                                            {topEvent ? (
                                                v2 ? <SectionHeader title={<>Mixes matching &ldquo;{query}&rdquo;</>} link={`Only ${topEvent.name}`} onLink={() => goEvent(topEvent)} /> : (
                                                <div className="sr-section-header">
                                                    <h2 className="search-results-h2">Mixes matching &ldquo;{query}&rdquo;</h2>
                                                    <button type="button" className="sr-show-all" onClick={() => goEvent(topEvent)}>
                                                        Only {topEvent.name}
                                                    </button>
                                                </div>
                                                )
                                            ) : (hasSplit || (topTrack && trackMixes.length > 0)) && (
                                                v2 ? <SectionHeader title={<>Other matches</>} /> : (
                                                <div className="sr-section-header">
                                                    <h2 className="search-results-h2">Other matches</h2>
                                                </div>
                                                )
                                            )}
                                            <div className="sr-browse-grid">
                                                {rest.map((m, i) => renderMixTile(m, 'sm', i, rest))}
                                            </div>
                                            <div ref={scopeSentinelRef} />
                                            {scopeFull.mixes?.loadingMore && <div className="sr-scope-loading-more"><span className="mix-play-spinner" /></div>}
                                        </div>
                                    )}

                                </>
                            );
                        })()}

                        {(() => {
                            // Scoped to the pill, the top entity reappears —
                            // its card is gone, so hiding it would drop the
                            // biggest match from its own list.
                            const list = scope === 'events' ? (groups.events || []) : otherEvents;
                            const visible = scope === 'all' || scope === 'mixes' || scope === 'events';
                            if (!visible || !list.length) return null;
                            const eventTiles = list.map((e, i) => (
                                <button className="sr-tile" key={`ev-${i}`} onClick={() => goEvent(e)}>
                                    {e.covers?.length >= 2 ? (
                                        <div className={`sr-art sr-art-rounded sr-art-collage sr-art-collage-${Math.min(e.covers.length, 4)}`}>
                                            {e.covers.slice(0, 4).map((c, ci) => <CoverArt src={c} size={24} key={ci} />)}
                                        </div>
                                    ) : (
                                        <Artwork src={e.covers?.[0] || null} shape="rounded" fallback={firstChar(e.name)} />
                                    )}
                                    <div className="sr-tile-title">{e.name}</div>
                                    <div className="sr-tile-sub">
                                        {eventKindLabel(e.kind, e.type)}
                                        {e.mixCount ? ` · ${e.mixCount} ${e.mixCount === 1 ? 'mix' : 'mixes'}` : ''}
                                    </div>
                                </button>
                            ));
                            return (
                                <div className="search-results-section">
                                    {/* No heading in the scoped view — the active
                                        pill already says it, same as Artists. */}
                                    {scope !== 'events' && (
                                        v2 ? <SectionHeader title={<>{eventSectionHeader(list)}</>} /> : (
                                        <div className="sr-section-header">
                                            <h2 className="search-results-h2">{eventSectionHeader(list)}</h2>
                                        </div>
                                        )
                                    )}
                                    {scope === 'all'
                                        ? <TileScroller>{eventTiles}</TileScroller>
                                        : <div className="sr-browse-grid">{eventTiles}</div>}
                                </div>
                            );
                        })()}

                        {showArtists && filteredArtists.length > 0 && (() => {
                            const artistTiles = filteredArtists.map((a, i) => (
                                <button className="sr-tile" key={entityId(a) || `a-${i}`} onClick={() => goArtist(a.name, entityId(a), a.artworkUrl)}>
                                    <Artwork src={a.artworkUrl} shape="circle" fallback={firstChar(a.name)} />
                                    <div className="sr-tile-title">{a.name}</div>
                                    <div className="sr-tile-sub">Artist</div>
                                </button>
                            ));
                            return (
                                <div className="search-results-section">
                                    {/* No "Artists" heading in the scoped view — the
                                        active pill already says it. Keep it in All. */}
                                    {scope !== 'artists' && (
                                        v2 ? <SectionHeader title={<>Artists</>} /> : (
                                        <div className="sr-section-header">
                                            <h2 className="search-results-h2">Artists</h2>
                                        </div>
                                        )
                                    )}
                                    {scope === 'artists'
                                        ? <div className="sr-browse-grid">{artistTiles}</div>
                                        : <TileScroller>{artistTiles}</TileScroller>}
                                </div>
                            );
                        })()}

                        {showLabels && labelEntities.length > 0 && (() => {
                            const labelTiles = labelEntities.map((l, i) => (
                                <button className="sr-tile" key={l.publicId || l.id || `l-${i}`} onClick={() => goLabel(l)}>
                                    <Artwork src={l.artworkUrl} shape="rounded" fallback={firstChar(l.name)} />
                                    <div className="sr-tile-title">{l.name}</div>
                                    <div className="sr-tile-sub">{l.trackCount ? `Label · ${l.trackCount} tracks` : 'Label'}</div>
                                </button>
                            ));
                            return (
                                <div className="search-results-section">
                                    {scope !== 'labels' && (
                                        v2 ? <SectionHeader title={<>Labels</>} /> : (
                                        <div className="sr-section-header">
                                            <h2 className="search-results-h2">Labels</h2>
                                        </div>
                                        )
                                    )}
                                    {scope === 'labels'
                                        ? <div className="sr-browse-grid">{labelTiles}</div>
                                        : <TileScroller>{labelTiles}</TileScroller>}
                                </div>
                            );
                        })()}
                    </>
                );
            })()}
        </div>
    );
};

export default SearchResultsPage;
